import { Box, Heading } from "@vinomofo/components";
import styled from "styled-components";
import { useCountdownTimer } from "../../hooks/use-countdown-timer";
import { useNextTimer } from "../../hooks/use-next-timer";
import { isWhiskymofo } from "../../utils/is-page";
import { useEffect, useRef } from "react";

const sizeConfig = {
  sm: {
    fontSize: ["11px", "14px"],
    lineHeight: ["12px", "13px"],
    gap: "6px",
    padding: ["1px", "5px"],
    minWidth: ["12px", "17px", "18px"],
    labelFontSize: "12px",
    labelMarginTop: "-8px",
    letterSpacing: "-0.5px",
  },
  md: {
    fontSize: ["16px", "20px", "25px"],
    lineHeight: ["100%", "132%", "132%"],
    gap: "10px",
    padding: "8px",
    minWidth: ["60px", "60px", "60px"],
    labelFontSize: "14px",
    labelMarginTop: "-12px",
  },
  lg: {
    fontSize: ["24px", "32px", "40px"],
    lineHeight: ["100%", "140%", "140%"],
    gap: "16px",
    padding: "12px",
    minWidth: ["80px", "80px", "80px"],
    labelFontSize: "16px",
    labelMarginTop: "-15px",
  },
};

const StyledTimerContainer = styled(Box)<{
  size: keyof typeof sizeConfig;
  isSpirit: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ size }) => sizeConfig[size as keyof typeof sizeConfig].gap};
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.heading};
  @media ${({ theme }) => theme.mq.max.tablet} {
    margin-right: 5px;
  }
  /* ${({ isSpirit }) =>
    !isSpirit &&
    `
    border: 1px solid rgb(217, 217, 217);
    border-bottom: none;
    border-radius: 10px 10px 0 0;
    padding-top: 3px;
  `} */
`;

const StyledTimerUnit = styled(Heading)<{
  size: keyof typeof sizeConfig;
}>`
  font-family: ${({ theme }) => theme.fonts.heading};
  text-align: center;
  justify-content: center;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.mofoPrimary};
  font-size: ${({ size }) => sizeConfig[size].fontSize[0]};
  line-height: ${({ size }) => sizeConfig[size].lineHeight[0]};
  font-weight: 500;
  font-style: normal;
  min-width: ${({ size }) => sizeConfig[size].minWidth[0]};
  @media ${({ theme }) => theme.mq.min.tablet} {
    font-size: ${({ size }) => sizeConfig[size].fontSize[1]};
    line-height: ${({ size }) => sizeConfig[size].lineHeight[1]};
    min-width: ${({ size }) => sizeConfig[size].minWidth[1]};
  }

  @media ${({ theme }) => theme.mq.min.desktop} {
    font-size: ${({ size }) => sizeConfig[size].fontSize[2]};
    line-height: ${({ size }) => sizeConfig[size].lineHeight[2]};
    min-width: ${({ size }) => sizeConfig[size].minWidth[2]};
  }
`;

const StyledTimerSeparator = styled(StyledTimerUnit)`
  margin: 0px;
  min-width: auto;
  padding: 0px;
  letter-spacing: -2px;
`;

const AlternativeTimerContainer = styled(Box)<{
  size: keyof typeof sizeConfig;
}>`
  display: flex;
  justify-content: center;
  gap: ${({ size }) => sizeConfig[size as keyof typeof sizeConfig].gap};
  margin: 0;
  @media ${({ theme }) => theme.mq.max.tablet} {
    gap: ${({ size }) => (size === "lg" ? "8px" : "4px")};
  }
`;

const StyledAlternativeTimerUnit = styled(Box)<{
  size: keyof typeof sizeConfig;
}>`
  display: inline-block;
  line-height: 1;
  padding: ${({ size }) => sizeConfig[size as keyof typeof sizeConfig].padding};
  font-family: ${({ theme }) => theme.fonts.specialHeading};
  font-size: ${({ size }) =>
    size === "sm" ? "48px" : size === "md" ? "72px" : "98px"};
  font-weight: 400;
  color: #fff;
  text-align: center;
  min-width: ${({ size }) =>
    sizeConfig[size as keyof typeof sizeConfig].minWidth};

  @media ${({ theme }) => theme.mq.max.tablet} {
    font-size: ${({ size }) =>
      size === "sm" ? "40px" : size === "md" ? "60px" : "90px"};
    padding: ${({ size }) => (size === "lg" ? "5px" : "3px")};
  }
`;

const StyledText = styled.span<{ size: keyof typeof sizeConfig }>`
  font-family: ${({ theme }) => theme.fonts.heading};
  display: block;
  font-size: ${({ size }) =>
    sizeConfig[size as keyof typeof sizeConfig].labelFontSize};
  font-weight: normal;
  margin-top: ${({ size }) =>
    sizeConfig[size as keyof typeof sizeConfig].labelMarginTop};
  text-align: center;
`;

const formatTimeValue = (value: number): string =>
  value.toString().padStart(2, "0");

const TimerSeparator = ({ size }: { size: keyof typeof sizeConfig }) => (
  <StyledTimerSeparator size={size}>:</StyledTimerSeparator>
);

const TimerUnit = ({
  value,
  size,
}: {
  value: number;
  size: keyof typeof sizeConfig;
}) => <StyledTimerUnit size={size}>{formatTimeValue(value)}</StyledTimerUnit>;

const AlternativeTimerUnit = ({
  value,
  label,
  size,
}: {
  value: number;
  label: string;
  size: keyof typeof sizeConfig;
}) => {
  return (
    <StyledAlternativeTimerUnit size={size} letterSpacing={"normal"}>
      {formatTimeValue(value)}
      <StyledText size={size}>{label}</StyledText>
    </StyledAlternativeTimerUnit>
  );
};

interface InlineTimerDisplayProps {
  dateTime?: string;
  isExpandedDisplay?: boolean;
  size?: keyof typeof sizeConfig;
  hideDays?: boolean;
  onTimerUpdate?: (newDateTime: string) => void;
}

const InlineTimerDisplay = ({
  dateTime,
  isExpandedDisplay = false,
  size = "md",
  hideDays = false,
  onTimerUpdate,
}: InlineTimerDisplayProps) => {
  const timeLeft = useCountdownTimer(dateTime || "");
  const { fetchNextTimer } = useNextTimer();
  const hasCalledApi = useRef(false);

  useEffect(() => {
    const handleTimerExpiry = async () => {
      if (
        isExpandedDisplay &&
        timeLeft &&
        Object.values(timeLeft).every((value) => value === 0) &&
        !hasCalledApi.current
      ) {
        hasCalledApi.current = true;

        const nextTimer = await fetchNextTimer();
        if (nextTimer && onTimerUpdate) {
          onTimerUpdate(nextTimer);
        }
      }
    };

    handleTimerExpiry();
  }, [isExpandedDisplay, timeLeft, fetchNextTimer, onTimerUpdate]);

  if (isExpandedDisplay) {
    return (
      <AlternativeTimerContainer size={size}>
        {(timeLeft?.days || 0) > 0 && (
          <AlternativeTimerUnit
            value={timeLeft?.days || 0}
            label="DAYS"
            size={size}
          />
        )}
        {(timeLeft?.hours || 0) > 5 && (
          <AlternativeTimerUnit
            value={timeLeft?.hours || 0}
            label="HRS"
            size={size}
          />
        )}
        <AlternativeTimerUnit
          value={timeLeft?.minutes || 0}
          label="MIN"
          size={size}
        />
        <AlternativeTimerUnit
          value={timeLeft?.seconds || 0}
          label="SEC"
          size={size}
        />
      </AlternativeTimerContainer>
    );
  }

  return (
    <StyledTimerContainer size={size} isSpirit={isWhiskymofo}>
      {!hideDays && <TimerUnit value={timeLeft?.days || 0} size={size} />}
      {!hideDays && <TimerSeparator size={size} />}
      <TimerUnit value={timeLeft?.hours || 0} size={size} />
      <TimerSeparator size={size} />
      <TimerUnit value={timeLeft?.minutes || 0} size={size} />
      <TimerSeparator size={size} />
      <TimerUnit value={timeLeft?.seconds || 0} size={size} />
    </StyledTimerContainer>
  );
};

export { InlineTimerDisplay };
